import { decodeToPcm } from './audioDecode.js';
function base64ToBytes(b64) {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
}
export class SpeechWorkerClient {
    worker;
    nextId = 1;
    engine;
    pending = new Map();
    constructor(worker, opts = {}) {
        this.worker = worker;
        this.engine = opts.engine ?? 'whisper';
        this.worker.addEventListener('message', (ev) => this.onMessage(ev.data));
        this.worker.addEventListener('error', (ev) => {
            const message = `speech worker error: ${ev.message}`;
            for (const [, p] of this.pending)
                p.reject(new Error(message));
            this.pending.clear();
        });
    }
    onMessage(msg) {
        const pending = this.pending.get(msg.id);
        if (!pending)
            return;
        if (msg.type === 'progress') {
            // Non-terminal: a warmup can report many of these before its
            // matching `ready`/`error` — don't delete the pending entry.
            if (pending.kind === 'warmup')
                pending.onProgress?.(msg);
            return;
        }
        this.pending.delete(msg.id);
        if (msg.type === 'error') {
            pending.reject(new Error(msg.message));
            return;
        }
        if (msg.type === 'ready' && pending.kind === 'warmup') {
            pending.resolve(msg.engine);
            return;
        }
        if (msg.type === 'result' && pending.kind === 'transcribe') {
            pending.resolve(msg.content);
            return;
        }
        // Response type doesn't match the pending request's kind — a bug in
        // this client's id bookkeeping, not something a caller can act on.
        pending.reject(new Error(`speech worker: mismatched response type "${msg.type}" for pending "${pending.kind}" request`));
    }
    /**
     * Load this client's engine (`this.engine`, e.g. `'whisper'` or
     * `'moonshine'`) without transcribing anything — resolves once the
     * worker reports `ready`, rejects on `error`. `onProgress` is called for
     * every `progress` message in between (a Cache Storage hit may skip
     * straight to `ready` with none). See module doc.
     */
    warmup(onProgress) {
        const id = this.nextId++;
        console.debug(`[legion-speech] client: warmup #${id} — engine=${this.engine}`);
        const req = { type: 'warmup', id, engine: this.engine };
        return new Promise((resolve, reject) => {
            this.pending.set(id, {
                kind: 'warmup',
                resolve: (engine) => {
                    console.debug(`[legion-speech] client: warmup #${id} ready (${engine})`);
                    resolve();
                },
                reject,
                onProgress,
            });
            this.worker.postMessage(req);
        });
    }
    transcribe(args) {
        const id = this.nextId++;
        // Decode HERE, on the main thread — `AudioContext` (WebAudio's
        // decoder) is Window-only and absent in the worker. We hand the
        // worker already-decoded 16 kHz mono PCM, transferring the buffer so
        // there's no copy.
        return (async () => {
            const bytes = base64ToBytes(args.audioBase64);
            console.debug(`[legion-speech] client: decoding clip #${id} (${bytes.length} bytes, ${args.mimeType}) on main thread…`);
            const { pcm, sampleRate } = await decodeToPcm(bytes, args.mimeType);
            console.debug(`[legion-speech] client: decoded #${id} → ${pcm.length} samples @ ${sampleRate}Hz; posting to worker`);
            const req = {
                type: 'transcribe',
                id,
                pcm,
                sampleRate,
                language: args.language,
                engine: this.engine,
            };
            return await new Promise((resolve, reject) => {
                this.pending.set(id, { kind: 'transcribe', resolve, reject });
                // Structured-clone COPY (no transferable list): the pcm is ~160 KB
                // for a few seconds of audio — a copy is negligible and avoids any
                // transfer edge case leaving the worker's view detached/undefined.
                this.worker.postMessage(req);
            });
        })();
    }
    /** Terminate the underlying worker and reject any in-flight requests. */
    dispose() {
        for (const [, p] of this.pending)
            p.reject(new Error('speech worker client disposed'));
        this.pending.clear();
        this.worker.terminate();
    }
}
